package com.tregz.tremenz.io.model

import com.tregz.tremenz.ui.icon.IconKey

sealed class ModelData {
    abstract val name: String
    abstract val genre: String?

    data class Audio(
        override val id: String,
        override val name: String,
        override val path: String,
        val sono: String?,
        override val type: String,
        override val year: Long,
        override val list: List<Track> = emptyList(),
        // A constructor property, so a poster that arrives later is a different value and the
        // repository's StateFlow emits it; a body property would be ignored by data class equality.
        override val posterUrl: String? = null
    ) : Media(id, name, path, type, year, list) {
        override val genre: String = path

        override val mediaUrl: String? = sono

        override fun toString(): String = name.replaceFirstChar {
            if (it.isLowerCase()) it.titlecase() else it.toString()
        }
    }

    abstract class Media(
        open val id: String,
        override val name: String,
        open val path: String,
        open val type: String,
        open val year: Long,
        open val list: List<Track> = emptyList()
    ) : ModelData() {
        abstract val posterUrl: String?
        abstract val mediaUrl: String?
    }

    data class Section(override val genre: String, val icon: IconKey) : ModelData() {
        var isSelected: Boolean = false
        override val name: String
            get() = genre

        var count: Int = 0

        val total: Int
            get() = ModelRepository.ITEMS.count {
                it is Video && it.genre.equals(genre, ignoreCase = true)
            }
    }

    data class Song(
        override val id: String,
        val artist: String,
        val featuring: String? = null,
        val mixedBy: String? = null,
        override val name: String,
        override val genre: String? = null,
        override val year: Long = 0L,
        val releasedAt: Long? = null,
        override val list: List<Track> = emptyList(),
        /** The catalogue's own artwork; the YouTube thumbnail stands in when there is none. */
        val artwork: String? = null
    ) : Media(id, name, "youtube", "youtube", year, list) {
        override val posterUrl: String = artwork ?: "https://img.youtube.com/vi/$id/hqdefault.jpg"

        val author: String = "$artist${if (!featuring.isNullOrBlank()) " ft $featuring" else ""}"

        override val mediaUrl: String = "https://www.youtube.com/watch?v=$id"

        override fun toString(): String = "$author - $name"
    }

    data class Track(
        val id: String,
        val artist: String,
        val cut: String?,
        val edition: String?,
        val featuring: String?,
        val mix: String?,
        val title: String,
        override val genre: String? = null
    ) : ModelData() {

        val author: String = "$artist${if (featuring != null) " ft $featuring" else ""}"
        override val name: String
            get() {
                val edit = if (edition != null) "${if (mix != null) " " else ""} $edition" else ""
                val version = if (mix != null || edition != null) " (${mix ?: ""}$edit)" else ""
                val visual = if (cut != null) " ($cut)" else ""
                return "$title$version$visual"
            }

        override fun toString(): String {
            return "$id $author - $name"
        }
    }

    data class Video(
        override val id: String,
        val host: String?,
        override val name: String,
        override val path: String,
        override val type: String,
        override val year: Long,
        override val list: List<Track> = emptyList(),
        override val posterUrl: String? = null
    ) : Media(id, name, path, type, year, list) {
        override val genre: String = path
        val file: String = "${year}_$name"

        // A mix with neither a host nor a file type has no playable address: null rather than a
        // malformed Dropbox link.
        override val mediaUrl: String? = when {
            !host.isNullOrBlank() -> "$host?&raw=1"
            type.isNotBlank() -> url(id, type)
            else -> null
        }

        private fun url(id: String, type: String): String =
            "https://www.dropbox.com/s/$id/$file.$type?&raw=1"

        override fun toString(): String = name.replaceFirstChar {
            if (it.isLowerCase()) it.titlecase() else it.toString()
        }
    }
}
